// Clips going up to the producer's library — the one way they do, from the
// room's shelf, the library page and the upload flow's clips form alike: a
// dropped folder becomes library folders under the one on screen, every file
// has its own progress row, and a finished batch can be reviewed or undone.
// The upload itself is useClipsUpload; this adds folders, the listen, and
// the status line the shelf shows.

import { createContext, useCallback, useContext, useMemo, useState } from "react";

import { LIBRARY_FOLDER_MAX_DEPTH, type MyFolder } from "@shared/api-types.js";
import { track } from "@web-shared/lib/analytics";
import type { MineShelf } from "@/components/MaterialLibrary";
import { clipFileViews, clipProgressTotals } from "@/lib/web-upload/clip-progress";
import { IntakeError, intakeFromDataTransfer, intakeFromFileList, type Intake, type IntakeFile } from "@/lib/web-upload/intake";
import type { ClipToSend } from "@/lib/web-upload/library-upload";
import { listenTo } from "@/lib/web-upload/listen";
import { clipsFromFiles, readFiles } from "@/lib/web-upload/read-files";
import { suggestForClips } from "@/lib/web-upload/suggest-clips";
import { useAuthGate } from "../auth";
import { useClipsUpload } from "./useClipsUpload";
import type { useMyClips } from "./useMyClips";

/** Root children are depth 0. */
export function folderDepth(folders: readonly MyFolder[], id: string): number {
  const byId = new Map(folders.map((f) => [f.id, f]));
  let d = 0;
  for (let cur = byId.get(id)?.parentId ?? null; cur !== null && d < 64; cur = byId.get(cur)?.parentId ?? null) d++;
  return d;
}

export type ClipSender = ReturnType<typeof useClipSender>;

/**
 * Set by a page that shows its own upload status (the library page): a
 * clips form inside it sends through the page's sender, into the folder on
 * screen, and the page's shelf shows the progress, Review and Undo.
 */
export const ClipSenderContext = createContext<{ sender: ClipSender; into: string | null } | null>(null);
export const useHostClipSender = (): { sender: ClipSender; into: string | null } | null => useContext(ClipSenderContext);

export function useClipSender(mine: ReturnType<typeof useMyClips>) {
  const gate = useAuthGate();
  const upload = useClipsUpload();
  const [dropProblem, setDropProblem] = useState<string | null>(null);
  const [listening, setListening] = useState(false);
  /** The files being listened to, by name — the rows before the upload opens. */
  const [listenNames, setListenNames] = useState<readonly string[]>([]);
  /** Files in the last drop that were not audio, and audio that would not read. */
  const [leftOut, setLeftOut] = useState<{ notAudio: number; unreadable: number } | null>(null);

  const createFolder = useCallback(
    async (name: string, parentId: string | null): Promise<MyFolder | null> => {
      try {
        return await mine.createFolder({ name, parentId });
      } catch (err) {
        setDropProblem(err instanceof Error ? err.message : "That folder couldn't be made.");
        return null;
      }
    },
    [mine],
  );

  /**
   * The folders a dropped tree needs, made under `into` and capped at the
   * library's depth (deeper levels fold into the deepest allowed): a folder
   * of breaks dropped on Mine becomes a Breaks folder, as Note's Externals
   * keep the phone's folders. Returns each file's folder.
   */
  const foldersFor = useCallback(
    async (files: readonly IntakeFile[], into: string | null): Promise<Map<string, string | null>> => {
      const baseDepth = into === null ? 0 : folderDepth(mine.folders, into) + 1;
      const made = new Map<string, string | null>([["", into]]);
      const out = new Map<string, string | null>();
      for (const f of files) {
        const parts = f.path.split("/").slice(0, -1).filter((p) => p.trim() !== "");
        const kept = parts.slice(0, Math.max(0, LIBRARY_FOLDER_MAX_DEPTH - baseDepth));
        let parent = into;
        let key = "";
        for (const part of kept) {
          key = key ? `${key}/${part}` : part;
          let id = made.get(key);
          if (id === undefined) {
            const existing = mine.folders.find((x) => x.parentId === parent && x.name.toLowerCase() === part.toLowerCase());
            const folder = existing ?? (await createFolder(part, parent));
            id = folder?.id ?? parent;
            made.set(key, id);
          }
          parent = id;
        }
        out.set(f.path, parent);
      }
      return out;
    },
    [mine.folders, createFolder],
  );

  /**
   * Clips on their way into the library, from anywhere: the folders their
   * paths ask for (under `into`), then the upload. `receipt`: the done line
   * clears itself after a while — a shelf's or the page's status line, not a
   * form's result screen.
   */
  const sendClips = useCallback(
    async (clips: readonly ClipToSend[], into: string | null, opts: { from: "upload" | "sidebar"; receipt: boolean }): Promise<void> => {
      setDropProblem(null);
      const where = await foldersFor(clips.map((c) => c.file), into);
      await upload.start(clips.map((c) => ({ ...c, folderId: where.get(c.file.path) ?? into })), opts.from);
      if (opts.receipt) {
        window.setTimeout(() => {
          upload.reset();
          setLeftOut(null);
        }, 8000);
      }
    },
    [foldersFor, upload],
  );

  /** Files handed over — dropped on the shelf or the page, or picked — go
   * straight into the library, guesses and all, into the folder on screen;
   * a dropped folder keeps its shape. `intake` is read before the sign-in
   * gate: a DataTransfer is only readable during its event. */
  const addIntake = useCallback(
    (intake: Promise<Intake>, into: string | null, via: "sidebar" | "page") => {
      setDropProblem(null);
      setLeftOut(null);
      gate.requireSignIn("library_add", "Sign in to add clips to your library", () => {
        void (async () => {
          try {
            const got = await intake;
            const { ok, bad } = await readFiles(got.files);
            if (ok.length === 0) {
              setDropProblem(
                bad.length > 0
                  ? `None of those could be read as audio (${bad.length} file${bad.length === 1 ? "" : "s"}). Seedbed takes WAV, AIFF, FLAC and MP3.`
                  : got.skipped > 0
                    ? `No audio in that drop — ${got.skipped} file${got.skipped === 1 ? "" : "s"} that ${got.skipped === 1 ? "isn't" : "aren't"} audio. Seedbed takes WAV, AIFF, FLAC and MP3.`
                    : "No audio in that drop. Seedbed takes WAV, AIFF, FLAC and MP3.",
              );
              return;
            }
            // Say what was left out, once the clips are in: files that were
            // not audio, and audio that would not read, told apart.
            setLeftOut({ notAudio: got.skipped, unreadable: bad.length });
            // A listen for what each one is — a courtesy with a budget; the
            // upload goes ahead with name-only tags if it fails.
            setListenNames(ok.map((f) => f.file.name));
            setListening(true);
            const heard = await listenTo(ok.map((f) => f.file), undefined, { features: true }).catch(() => null);
            setListening(false);
            track("web_upload_started", { value: ok.length, detail: via });
            await sendClips(clipsFromFiles(ok, suggestForClips(ok, heard)), into, { from: via === "page" ? "upload" : "sidebar", receipt: true });
          } catch (err) {
            console.error("[Seedbed] shelf drop failed:", err);
            setDropProblem(err instanceof IntakeError ? err.message : `Those files couldn't be opened${err instanceof Error && err.message ? ` (${err.message})` : ""}.`);
          }
        })();
      });
    },
    [gate, sendClips],
  );
  const addFiles = useCallback((dt: DataTransfer, into: string | null) => addIntake(intakeFromDataTransfer(dt), into, "sidebar"), [addIntake]);
  /** The page's Add files button: a FileList from a picker. */
  const addPicked = useCallback((list: FileList | File[], into: string | null) => addIntake(intakeFromFileList(list), into, "page"), [addIntake]);

  const adding = useMemo((): MineShelf["adding"] => {
    if (dropProblem) return { text: dropProblem, error: true };
    const p = upload.phase;
    // A row per file while anything is in flight, with the bytes, the clock
    // and the put's own words when one failed — the summary line alone said
    // "didn't upload" and nothing else.
    const files = clipFileViews({ phase: p, names: listening ? listenNames : upload.names, session: upload.session, jobs: upload.jobs, listening });
    const totals = clipProgressTotals(files);
    const progress: NonNullable<MineShelf["adding"]>["progress"] =
      files.length > 0 && p.kind !== "done"
        ? {
            files,
            stage: listening ? "listening" : p.kind === "converting" ? "converting" : p.kind === "sending" ? "sending" : p.kind === "finishing" ? "checking" : "failed",
            done: p.kind === "converting" ? p.done : undefined,
            total: p.kind === "converting" ? p.total : undefined,
            sentBytes: totals.sentBytes,
            totalBytes: totals.totalBytes,
            sendingSince: upload.sendingSince,
            failed: totals.failed,
            active: totals.active,
          }
        : undefined;
    const onRetry = upload.canRetry && (p.kind === "failed" || (p.kind === "sending" && totals.failed > 0 && !totals.active)) ? () => void upload.retry() : undefined;
    if (listening) return { text: `Listening to ${listenNames.length} file${listenNames.length === 1 ? "" : "s"}, to suggest tags…`, error: false, progress };
    if (p.kind === "converting") return { text: `Adding ${p.total} clip${p.total === 1 ? "" : "s"}… (${p.done + 1} of ${p.total})`, error: false, progress };
    if (p.kind === "sending") {
      // Counted in files, as the rows are: two clips of one audio share a put.
      const sent = files.length - totals.failed;
      if (totals.failed > 0 && !totals.active) {
        return {
          text: totals.failed === files.length ? `None of the ${files.length} uploaded.` : `${totals.failed} of ${files.length} didn't upload. The other ${sent} ${sent === 1 ? "is" : "are"} up.`,
          error: true,
          progress,
          onRetry,
        };
      }
      return { text: totals.failed > 0 ? `Uploading… ${totals.failed} failed so far.` : "Uploading…", error: false, progress };
    }
    if (p.kind === "finishing") return { text: "Checking they arrived intact…", error: false, progress };
    if (p.kind === "failed") return { text: p.message, error: true, progress, onRetry };
    if (p.kind === "done") {
      const n = p.clips.length;
      const parts: string[] = [];
      if (leftOut && leftOut.notAudio > 0) parts.push(`${leftOut.notAudio} file${leftOut.notAudio === 1 ? " that wasn't" : "s that weren't"} audio`);
      if (leftOut && leftOut.unreadable > 0) parts.push(`${leftOut.unreadable} that couldn't be read`);
      const already = p.already.length > 0 ? ` ${p.already.length} ${p.already.length === 1 ? "was" : "were"} already here.` : "";
      return {
        text: `Added ${n} clip${n === 1 ? "" : "s"}.${already}${parts.length > 0 ? ` Left out ${parts.join(" and ")}.` : ""}`,
        error: false,
        done: true,
        batch: n > 0 && p.clips[0]?.batchId ? { id: p.clips[0].batchId, count: n } : null,
      };
    }
    return null;
  }, [upload, dropProblem, listening, listenNames, leftOut]);

  return { addFiles, addPicked, sendClips, adding, upload, setDropProblem, createFolder };
}
